import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useSyncExternalStore,
} from 'react';
import { DispatchCard } from 'src/entities/dispatch';

import { BoardInteractionStore } from './boardInteractionStore';

/**
 * ドラッグ表示とカード選択を Presenter の state から切り離す。
 *
 * Why not 単一 value の context（src/contexts/OrderSelectionContext.tsx の形）:
 * value が変われば consumer 全部が再描画するので、「選択の変わったカードだけ描く」を
 * 実現できない。useSyncExternalStore でキー単位に購読する。
 *
 * Why not zustand などの導入: 必要なのはキー単位の購読だけで、React 19 組み込みの
 * useSyncExternalStore で足りる。依存を増やさない。
 */
const BoardInteractionContext = createContext<BoardInteractionStore | null>(null);

export const BoardInteractionProvider: FC<{ store: BoardInteractionStore; children: ReactNode }> = ({
  store,
  children,
}) => (
  <BoardInteractionContext.Provider value={store}>{children}</BoardInteractionContext.Provider>
);

export function useBoardInteractionStore(): BoardInteractionStore {
  const store = useContext(BoardInteractionContext);
  // Provider の外で使うと「選択が反映されない」だけの静かな不具合になるので落とす
  if (!store) throw new Error('BoardInteractionProvider の外で board interaction store を使っています');
  return store;
}

export function useIsCardSelected(cardId: string): boolean {
  const store = useBoardInteractionStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedCardIds.has(cardId),
  );
}

export function useIsRecentlyMoved(cardId: string): boolean {
  const store = useBoardInteractionStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().recentlyMovedCardIds.has(cardId),
  );
}

export function useDropPreviewIndex(rowId: string): number | null {
  const store = useBoardInteractionStore();
  return useSyncExternalStore(store.subscribe, () => {
    const preview = store.getState().dropPreview;
    return preview && preview.rowId === rowId ? preview.index : null;
  });
}

export function useActiveDragCard(): DispatchCard | null {
  const store = useBoardInteractionStore();
  // state 内の参照をそのまま返す（毎回新しいオブジェクトを作ると
  // useSyncExternalStore が無限ループになるため）
  return useSyncExternalStore(store.subscribe, () => store.getState().activeCard);
}

export function useDragCount(): number {
  const store = useBoardInteractionStore();
  return useSyncExternalStore(store.subscribe, () => store.getState().dragCount);
}
